import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, LinearProgress, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  DialogTitle, DialogContent, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import axios from 'axios';
import { apiUrl, downloadBlob, handleApiError } from './utils/api';
import { formatDate } from './utils/date';
import NoMatchingRecords from './components/NoMatchingRecords';
import AppDialog from './components/AppDialog';
import SearchSelect from './components/SearchSelect';

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
};
const num = (v, d = 2) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '';
};

/**
 * Ticket Detail report — the per-ticket line items behind the weekly reconciliation.
 * Columns: Date, Invoice, Company, Tons, FS-Code, Rate, Initial Total, Total after company, Rate/mile, Expense.
 * "Total after company" applies the company deduction (CEMEX 10%, Martin/LCR 8.75%, IVY 0%).
 */
export default function TicketReport() {
  const iso = (d) => d.toISOString().split('T')[0];
  const defaultStart = iso(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const defaultEnd = iso(new Date());
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [orderNo, setOrderNo] = useState('');
  const [basis, setBasis] = useState('FS_CODE'); // FS_CODE | JOB_CODE — which code prices/labels the rows
  const [rows, setRows] = useState(null);
  const [companyFilter, setCompanyFilter] = useState([]); // client-side Plant/Company filter ([] = all)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewId, setPreviewId] = useState(null);

  const load = async (b = basis) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiUrl}/api/ticket-report`, { params: { startDate, endDate, basis: b } });
      setRows(Array.isArray(res.data) ? res.data : []);
      setCompanyFilter([]); // reset the plant filter for the new result set
    } catch (err) {
      setError(handleApiError(err, 'Failed to load ticket report'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setError('');
    try {
      const res = await axios.get(`${apiUrl}/api/documents/report/pdf`, {
        params: { startDate, endDate, orderNo: orderNo || undefined },
        responseType: 'blob',
      });
      downloadBlob(res, `tickets-${startDate}-to-${endDate}.pdf`);
    } catch (err) {
      setError(handleApiError(err, 'Failed to download PDF report'));
    }
  };

  const clearFilters = () => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setOrderNo('');
    setRows(null);
    setCompanyFilter([]);
    setError('');
  };

  // Distinct plants/companies in the loaded set drive the filter dropdown; the table + totals below
  // reflect the selected plant (client-side, no re-fetch).
  const allRows = rows || [];
  const companies = Array.from(new Set(allRows.map((r) => r.company).filter(Boolean))).sort();
  const filteredRows = companyFilter.length === 0
    ? allRows
    : allRows.filter((r) => companyFilter.includes(r.company));
  const sum = (key) => filteredRows.reduce((s, r) => s + (Number(r[key]) || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Ticket Detail Report</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per-ticket revenue. Total after company applies the company deduction (CEMEX 10%, Martin/LCR 8.75%, IVY 0%).
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField label="Start Date" type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="End Date" type="date" value={endDate}
            onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="Order # (PDF filter)" value={orderNo}
            onChange={(e) => setOrderNo(e.target.value)} size="small" placeholder="optional" />
          <ToggleButtonGroup
            size="small" exclusive value={basis}
            onChange={(e, val) => { if (val) { setBasis(val); if (rows) load(val); } }}
          >
            <ToggleButton value="FS_CODE" sx={{ textTransform: 'none' }}>By FS Code</ToggleButton>
            <ToggleButton value="JOB_CODE" sx={{ textTransform: 'none' }}>By Job Code</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" onClick={() => load()} disabled={loading}>Generate</Button>
          <Button variant="outlined" onClick={clearFilters} disabled={loading}>Clear</Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}>PDF (scans)</Button>
          {rows && companies.length > 0 && (
            <Box sx={{ minWidth: 200 }}>
              <SearchSelect multiple label="Plant" placeholder="All plants"
                options={companies}
                getOptionLabel={(c) => c}
                getOptionValue={(c) => c}
                value={companyFilter}
                onChange={setCompanyFilter} />
            </Box>
          )}
          {rows && (
            <Typography variant="body2" color="text.secondary">
              ({companyFilter.length === 0 ? `${rows.length} tickets` : `${filteredRows.length} of ${rows.length} tickets`})
            </Typography>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Loading…</Typography>
        </Box>
      )}

      {rows && filteredRows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="right">Tons</TableCell>
                <TableCell>{basis === 'JOB_CODE' ? 'Job-Code' : 'FS-Code'}</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Initial Total</TableCell>
                <TableCell align="right">Total after company</TableCell>
                <TableCell align="right">Whole Driver Total</TableCell>
                <TableCell align="right">Driver Total</TableCell>
                <TableCell align="right">Rate/mile</TableCell>
                <TableCell align="right">Expense</TableCell>
                <TableCell align="center">Scan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r, i) => (
                <TableRow key={`${r.invoice}-${i}`} hover>
                  <TableCell>{formatDate(r.date)}</TableCell>
                  <TableCell>{r.invoice}</TableCell>
                  <TableCell>{r.company}</TableCell>
                  <TableCell align="right">{num(r.tons)}</TableCell>
                  <TableCell>{basis === 'JOB_CODE' ? r.jobCode : r.fsCode}</TableCell>
                  <TableCell align="right">{money(r.rate)}</TableCell>
                  <TableCell align="right">{money(r.initialTotal)}</TableCell>
                  <TableCell align="right">{money(r.totalAfterCompany)}</TableCell>
                  <TableCell align="right">{r.wholeDriverTotal != null ? money(r.wholeDriverTotal) : '—'}</TableCell>
                  <TableCell align="right">{r.driverTotal != null ? money(r.driverTotal) : '—'}</TableCell>
                  <TableCell align="right">{r.ratePerMile != null ? money(r.ratePerMile) : '—'}</TableCell>
                  <TableCell align="right">{r.expense != null ? money(r.expense) : '—'}</TableCell>
                  <TableCell align="center">
                    {r.documentId ? (
                      <IconButton size="small" title="Preview scanned ticket" onClick={() => setPreviewId(r.documentId)}>
                        <ImageSearchIcon fontSize="small" />
                      </IconButton>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={6} align="right"><strong>Totals</strong></TableCell>
                <TableCell align="right"><strong>{money(sum('initialTotal'))}</strong></TableCell>
                <TableCell align="right"><strong>{money(sum('totalAfterCompany'))}</strong></TableCell>
                <TableCell align="right"><strong>{money(sum('wholeDriverTotal'))}</strong></TableCell>
                <TableCell align="right"><strong>{money(sum('driverTotal'))}</strong></TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && rows && filteredRows.length === 0 && <NoMatchingRecords sx={{ mt: 2 }} />}

      <AppDialog open={previewId != null} onClose={() => setPreviewId(null)} fullWidth maxWidth="md">
        <DialogTitle>Scanned Ticket</DialogTitle>
        <DialogContent>
          {previewId != null && (
            <img
              src={`${apiUrl}/api/documents/${previewId}`}
              alt="Scanned ticket"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </AppDialog>
    </Box>
  );
}
